import React, { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';
import { Header } from '@mds/ui';
import {
    Box,
    Breadcrumbs,
    Button,
    Link,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EditBlock from '../EditBlock';

export interface AddEditHeaderProps {
    isNew: boolean;
    entityName: string;
    pathArea: string;
    returnUrl: string;
    editableHeader?: boolean;
    editablePlaceholder: string | undefined;
    errorMessage: string | undefined;
    value: string;
    onBlur?: (event: FocusEvent<any>) => void;
    onChange?: (value: string) => void;
    isResumeMessageVisible?: {
        onResume: () => void;
        onDiscard: () => void;
    };
}

export const AddEditHeader = (props: AddEditHeaderProps) => {
    const title = props.isNew
        ? `Add a new ${props.entityName}`
        : `Edit a ${props.entityName}`;
    const pathOperation = props.isNew
        ? `New ${props.entityName}`
        : `Edit ${props.entityName}`;
    const breadcrumbs = (
        <Breadcrumbs aria-label={`${props.pathArea} breadcrumb`}>
            <Link component={RouterLink} to={props.returnUrl}>
                {props.pathArea}
            </Link>
            <Typography
                variant="body1"
                sx={{
                    color: 'text.primary',
                }}
            >
                {pathOperation}
            </Typography>
        </Breadcrumbs>
    );
    const editableTitle = (
        <EditBlock
            id="article-add-edit-title"
            value={props.value}
            placeholder={props.editablePlaceholder}
            onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
                // https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode
                const enterKeyCode = 13;
                if (event.keyCode === enterKeyCode) {
                    event.preventDefault();
                }
            }}
            onChange={(
                event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => {
                props.onChange?.(event.target.value);
            }}
            onBlur={props.onBlur}
        />
    );
    const resumeMessage = props.isResumeMessageVisible ? (
        <Paper variant="outlined" sx={{ mt: 2 }}>
            <Box sx={{ p: 4 }}>
                <Stack spacing={2}>
                    <Typography variant="h5">
                        Unpublished content - Resume editing?
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        The content you were editing before was automatically
                        saved, so you don’t lose it. Do you want to resume
                        editing or discard the content and start from scratch?
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            onClick={() =>
                                props.isResumeMessageVisible?.onResume?.()
                            }
                        >
                            Resume editing
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() =>
                                props.isResumeMessageVisible?.onDiscard?.()
                            }
                        >
                            Discard content
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        </Paper>
    ) : null;

    return (
        <Box sx={{ mt: 2 }}>
            {props.editableHeader ? (
                <>
                    {breadcrumbs}
                    {resumeMessage}
                    <Box sx={{ mt: 2, width: 1 }}>{editableTitle}</Box>
                </>
            ) : (
                <>
                    <Header title={title} breadcrumbs={breadcrumbs} />
                    {resumeMessage}
                </>
            )}
            {props.errorMessage && (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: 'block' }}
                >
                    {props.errorMessage}
                </Typography>
            )}
        </Box>
    );
};

export default AddEditHeader;
